import { ethers } from 'ethers';
import { LEVER_SWAP_CONFIG } from './swapConfig';
import { safeNormalize, bigNumberFromUnknown } from './swapUtils';

const V2_PAIR_ABI = [
'function token0() view returns (address)',
'function token1() view returns (address)',
'function getReserves() view returns (uint112 reserve0, uint112 reserve1, uint32 blockTimestampLast)'];



function bpsDifference(a, b) {
  const left = BigInt(a || 0);
  const right = BigInt(b || 0);
  if (left === BigInt(0) && right === BigInt(0)) return 0;
  const denominator = left > BigInt(right) ? left : right;
  if (denominator === BigInt(0)) return 0;
  const diff = left > BigInt(right) ? left - BigInt(right) : right - BigInt(left);
  return Number(diff * BigInt(10000) / BigInt(denominator));
}

function getAmountOutV2(amountIn, reserveIn, reserveOut) {
  const amountInBN = BigInt(amountIn || 0);
  const reserveInBN = BigInt(reserveIn || 0);
  const reserveOutBN = BigInt(reserveOut || 0);

  if (amountInBN <= BigInt(0) || reserveInBN <= BigInt(0) || reserveOutBN <= BigInt(0)) {
    return BigInt(0);
  }

  // Standard Uniswap/Sushi V2 constant-product quote with 0.30% LP fee.
  const amountInWithFee = amountInBN * BigInt(997);
  return amountInWithFee * BigInt(reserveOutBN) / BigInt(reserveInBN * BigInt(1000) + BigInt(amountInWithFee));
}

export async function verifySellQuoteAgainstPool({ provider, amountTaxIn, sushiAssumedAmountOut }) {
  const result = {
    checked: false,
    ok: false,
    reason: null,
    pairAddress: safeNormalize(LEVER_SWAP_CONFIG.PAIR_ADDRESS),
    tokenTaxAddress: safeNormalize(LEVER_SWAP_CONFIG.PR_ADDRESS),
    wethAddress: safeNormalize(LEVER_SWAP_CONFIG.WETH_ADDRESS),
    amountTaxInRaw: String(amountTaxIn || '0'),
    sushiExpectedEthOutRaw: sushiAssumedAmountOut ? String(sushiAssumedAmountOut) : null,
    poolExpectedEthOutRaw: null,
    quoteDeviationBps: null,
    maxAllowedDeviationBps: LEVER_SWAP_CONFIG.MAX_QUOTE_DEVIATION_BPS,
    reserves: null,
    token0: null,
    token1: null
  };

  try {
    const amountInBN = BigInt(amountTaxIn || '0');
    const sushiOutBN = bigNumberFromUnknown(sushiAssumedAmountOut);

    if (amountInBN <= BigInt(0)) {
      result.reason = 'SELL quote verification skipped: amountTaxIn is zero.';
      return result;
    }
    if (!sushiOutBN || sushiOutBN <= BigInt(0)) {
      result.reason = 'SELL quote verification skipped: Sushi assumedAmountOut is missing or zero.';
      return result;
    }

    const pair = new ethers.Contract(result.pairAddress, V2_PAIR_ABI, provider);
    const [token0Raw, token1Raw, reserves] = await Promise.all([
    pair.token0(),
    pair.token1(),
    pair.getReserves()]
    );

    const token0 = safeNormalize(token0Raw);
    const token1 = safeNormalize(token1Raw);
    const tax = result.tokenTaxAddress.toLowerCase();
    const weth = result.wethAddress.toLowerCase();

    let reserveTax;
    let reserveWeth;

    if (token0.toLowerCase() === tax && token1.toLowerCase() === weth) {
      reserveTax = reserves.reserve0;
      reserveWeth = reserves.reserve1;
    } else if (token1.toLowerCase() === tax && token0.toLowerCase() === weth) {
      reserveTax = reserves.reserve1;
      reserveWeth = reserves.reserve0;
    } else {
      result.reason = 'Configured pair does not match StarTax/WETH.';
      result.token0 = token0;
      result.token1 = token1;
      return result;
    }

    const poolOut = getAmountOutV2(amountInBN, reserveTax, reserveWeth);
    const deviationBps = bpsDifference(poolOut, sushiOutBN);

    result.checked = true;
    result.token0 = token0;
    result.token1 = token1;
    result.reserves = {
      reserveTaxRaw: reserveTax.toString(),
      reserveWethRaw: reserveWeth.toString(),
      blockTimestampLast: reserves.blockTimestampLast?.toString?.() || String(reserves.blockTimestampLast || '')
    };
    result.poolExpectedEthOutRaw = poolOut.toString();
    result.quoteDeviationBps = deviationBps;
    result.ok = deviationBps <= LEVER_SWAP_CONFIG.MAX_QUOTE_DEVIATION_BPS;
    result.reason = result.ok ?
    'SELL quote matches pool within allowed deviation.' :
    `SELL quote deviation is too high: ${deviationBps} bps.`;
    return result;
  } catch (error) {
    result.reason = `SELL quote verification failed: ${error?.message || String(error)}`;
    return result;
  }
}
